'use client';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Tabs, Tab, IconButton, Tooltip, Badge } from '@mui/material';

import { useCompanyList } from '../../stores/companyStore';
import { usePoolList } from '../../stores/poolStore';
import { useTransitPointList } from '../../stores/transitPointStore';
import { useGeofenceList } from '../../stores/geofenceStore';
import { useVehicleStatusDashboardDynamic } from '../../stores/vehicleStatusStore';

import { fleetApi } from '../../api/axiosClient';
import { fetchRetase } from '../../stores/retaseStore';
import { useRouteTripList } from '../../stores/routeTripStore';
import dayjs from 'dayjs';

import VehicleData from './content/VehicleData';
import GeolocationData from './content/GeolocationData';
import NotificationSettings from './content/NotificationSettings';
import useVehicleNotifications from '../../hooks/useVehicleNotifications';
import { useLanguage } from '../../utils/i18n';
import { timezone } from '../../utils/timeZone';

import TuneIcon from '@mui/icons-material/Tune';

export default function MapContent({
  onSetMapCenter,
  onDataLoaded,
  onSelectVehicleFromSidebar,
  onSelectLocation,
  followedVehicleId,
  onFollowVehicle
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [userInfo, setUserInfo] = useState(null);
  const [allowedPoolIds, setAllowedPoolIds] = useState(null);
  const { t } = useLanguage();

  const [selectedGeoCompany, setSelectedGeoCompany] = useState(() => {
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        const compId = parsed.company_id ?? parsed.company?.id ?? parsed.company;
        if (compId) {
          return [String(compId)];
        }
      }
    } catch (e) {
      console.warn('Gagal membaca company dari local storage', e);
    }
    return [];
  });
  const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
  const { data: vehicleList = [], isLoading: loadVehicles, refetch, mutate } = useVehicleStatusDashboardDynamic(100, tz);

  const vehicleListRef = useRef(vehicleList);
  useEffect(() => {
    vehicleListRef.current = vehicleList;
  }, [vehicleList]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) setUserInfo(JSON.parse(raw));
    } catch (err) {
      console.error('load user failed:', err);
    }
  }, []);

  const { companyOptions: rawCompanyOptions } = useCompanyList();

  const companyOptions = useMemo(() => {
    const map = new Map();
    (rawCompanyOptions || []).forEach((c) => {
      const val = String(c.value ?? c.id ?? c);
      if (!map.has(val) && val !== '') {
        map.set(val, {
          value: val,
          label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
        });
      }
    });
    return Array.from(map.values());
  }, [rawCompanyOptions]);

  const allCompanyIdsParam = useMemo(() => {
    const ids = companyOptions.map((c) => c.value);
    return ids.length > 0 ? ids.join(',') : '';
  }, [companyOptions]);

  const { data: rawPoolList = [], isLoading: loadPool } = usePoolList(0, 1000, '', '', allCompanyIdsParam);
  const { data: rawTransitPointList = [], isLoading: loadTransit } = useTransitPointList(0, 1000, '', '', allCompanyIdsParam);
  const { data: rawGeofenceList = [], isLoading: loadGeofence } = useGeofenceList(0, 1000, '', '', allCompanyIdsParam);

  const { data: routeTripList = [] } = useRouteTripList(0, 1000, '', '', allCompanyIdsParam);

  useEffect(() => {
    if (routeTripList && routeTripList.length > 0) {
      const vehicleRoutes = {};

      const allLocs = [
        ...(Array.isArray(rawPoolList) ? rawPoolList : []),
        ...(rawTransitPointList?.data || rawTransitPointList || []),
        ...(rawGeofenceList?.data || rawGeofenceList || [])
      ];

      const getLatLon = (spatialId) => {
        const loc = allLocs.find((l) => String(l.id) === String(spatialId) || String(l.spatial_id) === String(spatialId));
        if (!loc) return { lat: 0, lon: 0 };
        return {
          lat: Number(loc.latitude || loc.lat || loc.centroid_lat || 0),
          lon: Number(loc.longitude || loc.lon || loc.centroid_lon || 0)
        };
      };

      routeTripList.forEach((route) => {
        const spatials = route.spatials || route.join?.spatials || [];
        const vehicles = route.vehicles || route.join?.vehicles || [];

        if (spatials.length === 0) return;

        // Find Origin (sequence_number 1 or the first one)
        const origin = spatials.find((s) => s.sequence_number === 1) || spatials[0];
        // Find Destination (highest sequence_number or the last one)
        const dest = spatials.reduce((prev, current) => (prev.sequence_number > current.sequence_number ? prev : current), spatials[0]);

        if (origin && dest && vehicles && Array.isArray(vehicles)) {
          const oCoords = getLatLon(origin.spatial_id);
          const dCoords = getLatLon(dest.spatial_id);

          const origin_lat = Number(origin.spatial_lat) || oCoords.lat;
          const origin_lon = Number(origin.spatial_lon) || oCoords.lon;
          const dest_lat = Number(dest.spatial_lat) || dCoords.lat;
          const dest_lon = Number(dest.spatial_lon) || dCoords.lon;

          vehicles.forEach((v) => {
            const vId = v.vehicle || v.vehicle_id;
            if (vId) {
              if (!vehicleRoutes[vId]) {
                vehicleRoutes[vId] = [];
              }
              vehicleRoutes[vId].push({
                route_id: route.id,
                origin_id: origin.spatial_id,
                origin_lat,
                origin_lon,
                dest_id: dest.spatial_id,
                dest_lat,
                dest_lon
              });
            }
          });
        }
      });
      localStorage.setItem('vehicle_routes', JSON.stringify(vehicleRoutes));
    }
  }, [routeTripList, rawPoolList, rawTransitPointList, rawGeofenceList]);

  const hasFetchedInitialRitase = useRef(false);

  useEffect(() => {
    const fetchActiveRitase = async () => {
      const currentVehicles = vehicleListRef.current;
      if (!currentVehicles || currentVehicles.length === 0) return;

      try {
        const startTime = dayjs().startOf('day').subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
        const endTime = dayjs().endOf('day').subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
        const activeRitaseMap = {};

        const chunkSize = 5;
        for (let i = 0; i < currentVehicles.length; i += chunkSize) {
          const chunk = currentVehicles.slice(i, i + chunkSize);
          await Promise.all(
            chunk.map(async (v) => {
              const vId = v.vehicle || v.id;
              const cId = v.company || localStorage.getItem('companyId') || allCompanyIdsParam;
              if (!vId || !cId) return;

              try {
                const response = await fetchRetase({
                  vehicleId: String(vId),
                  companyId: String(cId),
                  startTime,
                  endTime,
                  tz
                });

                let trips = [];
                if (Array.isArray(response?.data)) {
                  trips = response.data.retase;
                } else if (response?.data?.data.retase && Array.isArray(response.data.data)) {
                  trips = response.data.data;
                } else if (Array.isArray(response)) {
                  trips = response.retase || response.data?.retase || [];
                }

                const uniqueTripsMap = new Map();
                trips.forEach((tripData) => {
                  const routes = tripData.routes || [];
                  const spatialNames = routes.map((r) => r.spatial_name || r.spatialName).filter(Boolean);
                  const routeTitle = spatialNames.length > 0 ? spatialNames.join(' - ') : `Route ID: ${tripData.route_trip_id}`;

                  const details = tripData.details || tripData.routeDetails || [];
                  let earliestStart = null;
                  let latestEnd = null;

                  if (Array.isArray(details)) {
                    details.forEach((d) => {
                      const st = dayjs(d.start);
                      const en = dayjs(d.end);
                      if (!earliestStart || st.isBefore(earliestStart)) earliestStart = st;
                      if (!latestEnd || en.isAfter(latestEnd)) latestEnd = en;
                    });
                  }

                  const finalStart = earliestStart ? earliestStart.format('YYYY-MM-DD HH:mm:ss') : 'no-start';
                  const finalEnd = latestEnd ? latestEnd.format('YYYY-MM-DD HH:mm:ss') : 'no-end';

                  const uniqueKey = `${routeTitle}_${finalStart}_${finalEnd}`;
                  if (!uniqueTripsMap.has(uniqueKey)) {
                    uniqueTripsMap.set(uniqueKey, tripData);
                  }
                });

                const uniqueTrips = Array.from(uniqueTripsMap.values());
                uniqueTrips.forEach((trip) => {
                  const details = trip.details || trip.routeDetails || [];
                  if (Array.isArray(details)) {
                    details.forEach((d) => {
                      if (!activeRitaseMap[vId]) {
                        activeRitaseMap[vId] = [];
                      }

                      const isDuplicate = activeRitaseMap[vId].some((ex) => ex.start === d.start && ex.end === d.end);
                      if (!isDuplicate) {
                        activeRitaseMap[vId].push({ start: d.start, end: d.end });
                      }
                    });
                  }
                });
              } catch (err) {
                console.warn(`Failed to fetch ritase for vehicle ${vId}`, err);
              }
            })
          );
        }

        const payload = {
          data: activeRitaseMap,
          exp_at: dayjs().add(10, 'minute').format('YYYY-MM-DD HH:mm:ss')
        };
        localStorage.setItem('active_ritase_data', JSON.stringify(payload));
      } catch (err) {
        console.error('Failed to fetch active ritase', err);
      }
    };

    let interval;
    if (vehicleList.length > 0 && !hasFetchedInitialRitase.current) {
      hasFetchedInitialRitase.current = true;
      fetchActiveRitase();
      interval = setInterval(fetchActiveRitase, 10 * 60 * 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleList.length > 0, allCompanyIdsParam]);

  // Notification logic extracted to useVehicleNotifications hook
  const loadedStatusMap = useVehicleNotifications(vehicleList);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (typeof refetch === 'function') {
        refetch();
      } else if (typeof mutate === 'function') {
        mutate();
      } else {
        console.warn('MapContent: Auto-refresh tidak menemukan fungsi refetch/mutate dari hook.');
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [refetch, mutate]);

  useEffect(() => {
    if (!userInfo || !userInfo.id || allowedPoolIds !== null) return;

    const fetchAccess = async () => {
      try {
        const res = await fleetApi.get(`/user-map-access/${userInfo.id}`);
        const mapAccess = res?.data ?? [];
        const ids = mapAccess.map((a) => a.pool_id);
        setAllowedPoolIds(ids);
      } catch (err) {
        console.error('fetch user access failed', err);
        setAllowedPoolIds([]);
      }
    };

    fetchAccess();
  }, [userInfo, allowedPoolIds]);

  const finalPoolList = useMemo(() => {
    if (!Array.isArray(rawPoolList) || rawPoolList.length === 0) return [];

    if (allowedPoolIds === null || allowedPoolIds.length === 0) {
      return rawPoolList;
    }

    const filtered = rawPoolList.filter((p) => allowedPoolIds.includes(p.id));
    return filtered.length > 0 ? filtered : rawPoolList;
  }, [rawPoolList, allowedPoolIds]);

  const handleTabChange = (event, newValue) => {
    if (newValue !== false) {
      setActiveTab(newValue);
    }
  };

  const handleSelectVehicle = (vehicle) => {
    if (!vehicle) return;

    if (typeof onSelectVehicleFromSidebar === 'function') {
      onSelectVehicleFromSidebar(vehicle);
    } else {
      const lat = vehicle.lat ?? vehicle.latitude ?? vehicle.centroid_lat ?? vehicle.info?.centroid_lat;
      const lon = vehicle.lon ?? vehicle.lng ?? vehicle.longitude ?? vehicle.centroid_lon ?? vehicle.info?.centroid_lon;

      if (lat != null && lon != null) {
        if (onSetMapCenter) onSetMapCenter({ lat: Number(lat), lon: Number(lon) });
      }
    }
  };

  const isLoading = loadVehicles || loadPool || loadTransit || loadGeofence;

  const combinedLocations = useMemo(() => {
    const isAllSelected = !selectedGeoCompany || selectedGeoCompany.length === 0;
    const selectedIdsStr = selectedGeoCompany.map(String);

    const filterByCompanyAndStatus = (loc) => {
      // Jangan tampilkan jika statusnya false (tidak aktif)
      if (loc.status === false || loc.status === 'false' || loc.status === 0) return false;

      if (isAllSelected) return true;
      const locCompanyId = String(loc.company_id || loc.company || loc._companyId || '');
      return selectedIdsStr.includes(locCompanyId);
    };

    const pools = finalPoolList
      .filter(filterByCompanyAndStatus)
      .map((p) => ({ ...p, location_type: 'PIT', _companyId: String(p.company_id || '') }));

    const transits = (rawTransitPointList?.data || rawTransitPointList || [])
      .filter(filterByCompanyAndStatus)
      .map((t) => ({ ...t, location_type: 'Jetty', _companyId: String(t.company_id || '') }));

    const geofences = (rawGeofenceList?.data || rawGeofenceList || [])
      .filter(filterByCompanyAndStatus)
      .map((g) => ({ ...g, location_type: 'Geofence', _companyId: String(g.company_id || '') }));

    return [...pools, ...transits, ...geofences];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalPoolList, rawPoolList, rawTransitPointList, rawGeofenceList, selectedGeoCompany]);

  useEffect(() => {
    if (typeof onDataLoaded === 'function') {
      onDataLoaded({
        vehicles: vehicleList,
        locations: combinedLocations
      });
    }
  }, [vehicleList, combinedLocations, onDataLoaded]);

  const handleShowSetNotif = () => {
    setActiveTab(2);
  };

  const isTabNotifActive = activeTab === 2;

  const filteredCombinedLocations = combinedLocations;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#ffffff' }}>
      {/* HEADER TABS */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
        <Tabs
          value={isTabNotifActive ? false : activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            flexGrow: 1,
            minHeight: 48,
            fontSize: 12,
            '& .MuiTab-root': {
              color: 'rgba(64, 63, 63, 0.6)',
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 48
            },
            '& .Mui-selected': { color: '#485D9A !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#485D9A', height: 3 }
          }}
        >
          <Tab
            label={
              <Badge
                badgeContent={vehicleList.length}
                color="#485D9A"
                max={99}
                sx={{ '& .MuiBadge-badge': { right: -2, top: -5, bgcolor: '#485D9A', color: '#ffffff' } }}
              >
                {t('mapsTracking.vehicles', 'Vehicles')}
              </Badge>
            }
            value={0}
          />
          <Tab
            label={
              <Badge
                badgeContent={filteredCombinedLocations.length}
                max={99}
                sx={{ '& .MuiBadge-badge': { right: -2, top: -5, bgcolor: '#485D9A', color: '#ffffff' } }}
              >
                {t('mapsTracking.geolocation', 'Geolocation')}
              </Badge>
            }
            value={1}
          />
        </Tabs>

        {/* ICON TUNE */}
        <Tooltip title={t('mapsTracking.notificationSettings', 'Pengaturan Notifikasi')}>
          <IconButton
            onClick={handleShowSetNotif}
            sx={{
              borderRadius: 0,
              minWidth: 48,
              minHeight: 48,
              color: isTabNotifActive ? '#485D9A' : 'rgba(64, 63, 63, 0.6)',
              borderBottom: isTabNotifActive ? '3px solid #485D9A' : '3px solid transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(72, 93, 154, 0.08)'
              }
            }}
          >
            <TuneIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {isLoading && vehicleList.length === 0 ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}
          ></Box>
        ) : (
          <>
            <Box sx={{ display: activeTab === 0 ? 'block' : 'none', height: '100%' }}>
              <VehicleData
                vehicleList={vehicleList}
                onSelectVehicle={handleSelectVehicle}
                followedVehicleId={followedVehicleId}
                onFollowVehicle={onFollowVehicle}
                loadedStatusMap={loadedStatusMap}
              />
            </Box>

            <Box sx={{ display: activeTab === 1 ? 'block' : 'none', height: '100%' }}>
              {/* GeolocationData sudah TIDAK perlu menerima poolList, transitPointList secara mentah
                  KITA bisa melempar data yang sudah diolah dan difilter */}
              <GeolocationData
                preFilteredLocations={filteredCombinedLocations} // <- Kirimkan data yang sudah matang
                vehicleList={vehicleList}
                onSelectLocation={onSelectLocation}
                onSelectVehicle={handleSelectVehicle}
              />
            </Box>
            <Box sx={{ display: activeTab === 2 ? 'block' : 'none', height: '100%' }}>
              <NotificationSettings
                companyOptions={companyOptions}
                selectedGeoCompany={selectedGeoCompany}
                onChangeGeoCompany={setSelectedGeoCompany}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
